import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { hubFetch } from '@/api/client';
import type {
  ElevationDecision,
  ElevationRequest,
  ElevationStatus,
  PermissionPreset,
  ScopeCandidate,
  SubmitElevationCommand,
} from '@/api/types';
import { useEnvStore } from '@/env/envStore';
import type { HubEnv } from '@/env/types';

export interface ElevationListFilter {
  env?: HubEnv;
  status?: ElevationStatus;
  requester?: string;
  preset?: string;
  fromEpochMs?: number;
  toEpochMs?: number;
  limit?: number;
}

export function useElevationPresets() {
  return useQuery({
    queryKey: ['elevation', 'presets'],
    queryFn: () => hubFetch<PermissionPreset[]>('/api/elevation/presets'),
    staleTime: 5 * 60_000,
  });
}

export interface UseElevationRequestsOptions {
  /**
   * When false, TanStack skips the network call entirely (`enabled: false`).
   * Used by the left-nav badge so non-approvers — who would only see their
   * own requests anyway — don't fire `GET /api/elevation/requests` on every
   * shell render. Defaults to true so existing call sites (history table,
   * PendingDecisions inside the approver page) stay unchanged.
   */
  enabled?: boolean;
}

export function useElevationRequests(
  filter: ElevationListFilter = {},
  options: UseElevationRequestsOptions = {},
) {
  return useQuery({
    queryKey: ['elevation', 'requests', filter],
    queryFn: () =>
      hubFetch<ElevationRequest[]>('/api/elevation/requests', {
        searchParams: {
          env: filter.env,
          status: filter.status,
          requester: filter.requester,
          preset: filter.preset,
          from: filter.fromEpochMs,
          to: filter.toEpochMs,
          limit: filter.limit ?? 200,
        },
      }),
    // No `refetchInterval` — the history view doesn't need to poll. A
    // running approval flow churns these records on the order of minutes
    // to hours, and the prior 30s timer made the table flash + interrupted
    // the operator's scrolling. The query still refetches whenever:
    //   - the filter object changes (queryKey-driven invalidation)
    //   - a mutation explicitly invalidates `['elevation', 'requests']`
    //     (submit / decide / revoke each do this)
    //   - the user clicks "Refresh" in the ErrorState or by interacting
    //     with `query.refetch()` from the UI
    // `staleTime` keeps the cache hot across tab/route switches so we
    // don't re-fetch on every focus event either.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: options.enabled ?? true,
  });
}

export function usePendingElevationRequests(options: UseElevationRequestsOptions = {}) {
  const env = useEnvStore((s) => s.env);
  return useQuery({
    queryKey: ['elevation', 'pending', env],
    queryFn: () => hubFetch<ElevationRequest[]>('/api/elevation/requests/pending'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: options.enabled ?? true,
  });
}

export function useElevationScopeCandidates(presetKey: string | undefined) {
  // The response depends on (presetKey, env) — the API resolves the env
  // from the X-Hub-Env header `hubFetch` attaches, but TanStack only
  // re-keys when a queryKey value changes. Including env in the queryKey
  // is what keeps env-A's candidates from masquerading as env-B's after
  // the env-switcher flips. A consequence (not an explicit reset in the
  // form): when env flips, the new query key has no cached data yet, so
  // `candidatesByKind` starts empty, `availableKinds` is empty, the
  // derived `scopeKind` falls back to null, and any previously-picked
  // `scopeId` no longer appears in `visibleCandidates` and is dropped by
  // the same derivation. No explicit setState is needed.
  const env = useEnvStore((s) => s.env);
  return useQuery({
    queryKey: ['elevation', 'scope-candidates', env, presetKey ?? null],
    queryFn: () =>
      hubFetch<ScopeCandidate[]>('/api/elevation/scope-candidates', {
        searchParams: { presetKey },
      }),
    // Candidates come from the (embedded) manifest + the static EHR sub
    // list; safe to cache aggressively across the page session.
    staleTime: 5 * 60_000,
    enabled: presetKey !== undefined,
  });
}

export function useSubmitElevation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cmd: SubmitElevationCommand) =>
      hubFetch<ElevationRequest>('/api/elevation/requests', { method: 'POST', json: cmd }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['elevation', 'requests'] });
      void qc.invalidateQueries({ queryKey: ['elevation', 'pending'] });
    },
  });
}

export function useDecideElevation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: ElevationDecision }) =>
      hubFetch<ElevationRequest>(`/api/elevation/requests/${encodeURIComponent(id)}/decision`, {
        method: 'POST',
        json: decision,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['elevation', 'requests'] });
      void qc.invalidateQueries({ queryKey: ['elevation', 'pending'] });
    },
  });
}

export function useRevokeElevation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      hubFetch<ElevationRequest>(`/api/elevation/requests/${encodeURIComponent(id)}/revoke`, {
        method: 'POST',
        json: { reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['elevation', 'requests'] });
      void qc.invalidateQueries({ queryKey: ['elevation', 'pending'] });
    },
  });
}
